import type { GeoJsonProperties } from 'geojson';

import { useNavigation } from '@atom/hooks/useNavigation';
import { getKmlSvg } from '@atom/map';
import { Icon, IconButton } from '@atom/mui';
import { CloseIcon } from '@atom/mui/icons';
import type { KmlLayer } from '@atom/types/map';

import { useKmlLayers } from './KmlLayersProvider';

import classes from './kml.module.css';

type ParsedPlacemark = {
  title: string;
  subtitle: string;
  markerId: number | null;
  attributes: Array<{ label: string; value: string }>;
};

const parsePlacemark = (
  layer: KmlLayer,
  properties: GeoJsonProperties,
): ParsedPlacemark =>
  layer.extendedData.reduce<ParsedPlacemark>(
    (acc, entry) => {
      const raw = properties?.[entry.key];
      const value = raw === null || raw === undefined ? '' : String(raw);

      switch (entry.type) {
        case 'TITLE':
          return { ...acc, title: value };
        case 'SUBTITLE':
          return { ...acc, subtitle: value };
        case 'MARKER_ID': {
          const parsed = Number(value);
          return {
            ...acc,
            markerId:
              value.trim() === '' || Number.isNaN(parsed)
                ? acc.markerId
                : parsed,
          };
        }
        case 'ATTRIBUTE':
          return {
            ...acc,
            attributes: [...acc.attributes, { label: entry.name, value }],
          };
        default:
          return acc;
      }
    },
    { title: '', subtitle: '', markerId: layer.markerId, attributes: [] },
  );

const KmlSidebar = () => {
  const { selectedPlacemark, clearPlacemark } = useKmlLayers();
  const [, { updateSearchParams }] = useNavigation();

  const handleClose = () => {
    clearPlacemark();
    updateSearchParams({ view: 'layers' });
  };

  if (!selectedPlacemark) {
    return null;
  }

  const { layer, properties } = selectedPlacemark;
  const { title, subtitle, markerId, attributes } = parsePlacemark(
    layer,
    properties,
  );
  const icon = markerId === null ? null : getKmlSvg(markerId);

  return (
    <div className={classes.placemarkContainer}>
      <div className={classes.placemarkHeader}>
        <div className={classes.placemarkTitleContainer}>
          {icon && <img className={classes.placemarkIcon} src={icon} alt="" />}
          <div>
            <div className={classes.placemarkTitle}>{title || layer.name}</div>
            {subtitle && (
              <div className={classes.placemarkSubtitle}>{subtitle}</div>
            )}
          </div>
        </div>
        <IconButton edge="end" onClick={handleClose}>
          <Icon icon={CloseIcon} />
        </IconButton>
      </div>
      <div className={classes.placemarkBody}>
        {attributes.length === 0 ? (
          <div className={classes.emptyState}>No attributes available.</div>
        ) : (
          attributes.map(({ label, value }, index) => (
            <div className={classes.attributeRow} key={`${label}-${index}`}>
              <div className={classes.attributeLabel}>{label}</div>
              <div className={classes.attributeValue}>{value || '-'}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default KmlSidebar;
